import CallIcon from '@mui/icons-material/Call';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import LocationOnOutlinedIcon from '@mui/icons-material/LocationOnOutlined';
import SmsOutlinedIcon from '@mui/icons-material/SmsOutlined';
import { useTheme } from '@mui/material';
import { Box, Grid2 as Grid, List, ListItem, ListItemIcon, ListItemText, Paper, Typography } from '@mui/material';
import React from 'react';
import { FaFacebook, FaInstagram } from 'react-icons/fa';

const contactSx = {
    contactInfo: {
        position: 'relative',
        padding: 0,
        marginBottom: '10px',
        textAlign: 'left',
        '& .MuiListItem-gutters': {
            padding: 0,
            marginBottom: '5px',
        },
        '& ListItemText': {
            fontWeight: '300',
        },
        '& :hover': {
            color: 'primary.main',
            transition: 'all, .2s',
        },
    },
    contactInfoDark: {
        color: 'secondary.contrastText',
        '& .MuiSvgIcon-root': {
            color: 'common.white',
        },
    },
    socialIcons: {
        '& .MuiSvgIcon-root': {
            fontSize: 32,
            marginRight: '25px',
        },
        '& :hover': {
            cursor: 'pointer',
        },
    },
} as const;

interface ContactProps {
    location?: string;
    mapLink?: string;
    address?: string;
    phone: string;
    email?: string;
    whatsapp?: string;
    hideSocial?: boolean;
    darkBackground?: boolean;
    disableTopMargin?: boolean;
}

export default function ContactDetails({
    location,
    mapLink,
    address,
    phone,
    email,
    whatsapp,
    darkBackground,
    hideSocial,
    disableTopMargin,
}: ContactProps) {
    const theme = useTheme();

    const ListItemLink = (props: any) => {
        return <ListItem component="a" {...props} />;
    };

    return (
        <Paper
            elevation={0}
            sx={{ backgroundColor: darkBackground ? 'inherit' : 'rgba(0,0,0,0)', mt: disableTopMargin ? 0 : 4 }}
        >
            {location && (
                <Typography variant="h5" sx={{ mb: 2, color: darkBackground ? 'white' : 'text.primary' }}>
                    {location}
                </Typography>
            )}
            <List style={{ paddingLeft: 15 }}>
                {address && (
                    <ListItem sx={[contactSx.contactInfo, darkBackground && contactSx.contactInfoDark]}>
                        <ListItemLink href={mapLink} target="_blank">
                            <ListItemIcon>
                                <LocationOnOutlinedIcon />
                            </ListItemIcon>
                            <ListItemText
                                style={{ color: darkBackground ? 'white' : theme.palette.text.soft }}
                                primary={address}
                            />
                        </ListItemLink>
                    </ListItem>
                )}
                {phone && (
                    <ListItem sx={[contactSx.contactInfo, darkBackground && contactSx.contactInfoDark]}>
                        <ListItemLink href={`tel:${phone}`}>
                            <ListItemIcon>
                                <CallIcon />
                            </ListItemIcon>
                            <ListItemText
                                style={{ color: darkBackground ? 'white' : theme.palette.text.soft }}
                                primary={phone}
                            />
                        </ListItemLink>
                    </ListItem>
                )}
                {whatsapp && (
                    <ListItem sx={[contactSx.contactInfo, darkBackground && contactSx.contactInfoDark]}>
                        <ListItemLink
                            href={`https://wa.me/${whatsapp.replace(/[^0-9]/g, '')}`}
                            target="_blank"
                            rel="noopener noreferrer"
                        >
                            <ListItemIcon>
                                <SmsOutlinedIcon />
                            </ListItemIcon>
                            <ListItemText
                                style={{ color: darkBackground ? 'white' : theme.palette.text.soft }}
                                primary={whatsapp}
                                secondary="SMS or WhatsApp — calls not supported"
                                secondaryTypographyProps={{
                                    style: {
                                        color: darkBackground ? 'rgba(255,255,255,0.7)' : theme.palette.text.secondary,
                                    },
                                }}
                            />
                        </ListItemLink>
                    </ListItem>
                )}
                {email && (
                    <ListItem sx={[contactSx.contactInfo, darkBackground && contactSx.contactInfoDark]}>
                        <ListItemLink href={`mailto:${email}`}>
                            <ListItemIcon>
                                <EmailOutlinedIcon />
                            </ListItemIcon>
                            <ListItemText
                                style={{ color: darkBackground ? 'white' : theme.palette.text.soft }}
                                primary={email}
                            />
                        </ListItemLink>
                    </ListItem>
                )}
            </List>
            {!hideSocial && (
                <Grid container>
                    <Grid size={{ xs: 12 }} style={{ paddingLeft: 15 }}>
                        <Box sx={contactSx.socialIcons}>
                            <a href="https://facebook.com/airwhitsunday" aria-label="Facebook Link">
                                <FaFacebook style={{ color: darkBackground ? 'white' : theme.palette.text.soft }} />
                            </a>
                            <a href="https://instagram.com/air_whitsunday/" aria-label="Instagram Link">
                                <FaInstagram style={{ color: darkBackground ? 'white' : theme.palette.text.soft }} />
                            </a>
                        </Box>
                    </Grid>
                </Grid>
            )}
        </Paper>
    );
}
